import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ScrollText } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { PagoRegistrado } from '@/shared/api/tipos';
import { ModalDeCobroDeTitulos } from '@/modules/titulos/ModalDeCobroDeTitulos';
import { enMes, type Deudor } from '@/modules/titulos/piezas';

const CEDULA_O_RUC = /^(\d{10}|\d{13})$/;

/**
 * Los títulos del Art. 8 de la persona, debajo de su tasa y con el mismo aspecto (EPAGAL, 4-nov):
 * la cajera ve todo lo que debe sin cambiar de pantalla.
 *
 * Se ven aquí pero se cobran aparte, con su botón: van por el punto 980, sin mora y pueden
 * pagarlos otros, así que llevan su propio cobro, su recibo y su factura. Solo salen los de esa
 * cédula; los que el SIRE dejó sin cédula se siguen buscando por nombre en Títulos.
 */
export function GrupoDelArt8({
  identificacion,
  puedeVer,
  puedeCobrar,
  alCobrado,
}: {
  identificacion: string;
  puedeVer: boolean;
  puedeCobrar: boolean;
  alCobrado: (pago: PagoRegistrado) => void;
}) {
  const [plegado, setPlegado] = useState(false);
  const [cobrando, setCobrando] = useState(false);

  const deudores = useQuery({
    queryKey: ['titulos-deudores', identificacion],
    queryFn: () =>
      api.get<{ deudores: Deudor[]; hayMas: boolean }>('/titulos/deudores', {
        query: { texto: identificacion },
      }),
    enabled: puedeVer && CEDULA_O_RUC.test(identificacion),
  });

  const titulos = (deudores.data?.deudores ?? [])
    .filter((d) => d.identificacion === identificacion)
    .flatMap((d) => d.titulos);

  if (titulos.length === 0) {
    return null;
  }

  const saldo = titulos.reduce((suma, t) => suma + t.capital, 0);
  const nombre = 'Recuperación de cartera · Art. 8';

  return (
    <section
      aria-label={nombre}
      className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm"
    >
      <div className="flex items-center gap-3 border-b border-armazon-200 bg-armazon-100 px-5 py-2">
        <ScrollText className="size-4 shrink-0 text-armazon-600" aria-hidden />
        <button
          type="button"
          onClick={() => setPlegado(!plegado)}
          aria-expanded={!plegado}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-[11px] font-bold uppercase text-armazon-800"
        >
          <ChevronRight
            className={`size-3.5 shrink-0 transition-transform ${plegado ? '' : 'rotate-90'}`}
            aria-hidden
          />
          <span className="truncate">{nombre}</span>
          <span className="shrink-0 font-semibold text-armazon-500">
            · {titulos.length} {titulos.length === 1 ? 'título' : 'títulos'}
          </span>
        </button>
        <span className="importe shrink-0 text-sm font-bold text-armazon-900">
          ${saldo.toFixed(2)}
        </span>
      </div>

      {!plegado && (
        <>
          <div className="max-h-72 divide-y divide-armazon-100 overflow-y-auto">
            {titulos.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <span className="importe w-24 shrink-0 text-xs font-semibold text-armazon-800">
                  {t.codigo ?? t.id}
                </span>
                <span className="min-w-0 flex-1 basis-52">
                  <span className="importe block text-[11px] text-armazon-700">
                    {enMes(t.desde)}
                    {t.hasta && t.hasta !== t.desde ? ` – ${enMes(t.hasta)}` : ''}
                  </span>
                  <span className="block truncate text-[10px] uppercase text-armazon-400">
                    {t.meses != null ? `${t.meses} ${t.meses === 1 ? 'mes' : 'meses'}` : 'Sin meses'}
                    {t.anexos ? ` · ${t.anexos}` : ''}
                  </span>
                </span>
                <span className="importe w-24 shrink-0 text-right text-sm font-semibold text-armazon-800">
                  ${t.capital.toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-armazon-100 px-5 py-3">
            <p className="text-[10px] uppercase leading-relaxed text-armazon-500">
              Se cobran aparte: sin mora, con su propio recibo y factura.
            </p>
            {puedeCobrar && (
              <button
                type="button"
                onClick={() => setCobrando(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-marca-600 px-4 py-2 text-[11px] font-semibold uppercase text-white transition-colors hover:bg-marca-700"
              >
                <ScrollText className="size-3.5" aria-hidden />
                Cobrar Art. 8
              </button>
            )}
          </div>
        </>
      )}

      {cobrando && (
        <ModalDeCobroDeTitulos
          textoInicial={identificacion}
          alCerrar={() => setCobrando(false)}
          alCobrado={(pago) => {
            setCobrando(false);
            alCobrado(pago);
          }}
        />
      )}
    </section>
  );
}
